import React, { useState, useMemo } from "react";
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from "./ui/button";
import TestFlowVisualizer from "./ui/TestFlowVisualizer";
import { renderStep } from "../utils/RenderStep";
import './TestScenarioDetail.css';

const DatasetManager = ({ test, onUpdateDataset }) => {
  const [dataset, setDataset] = useState(test.dataset || {});
  const { t } = useTranslation();

  const handleSave = async () => {
    try {
      await onUpdateDataset(test.id, dataset);
      alert(t('testScenarioDetail.datasetUpdated'));
    } catch (error) {
      console.error(t('testScenarioDetail.datasetUpdateError'), error);
      alert(t('testScenarioDetail.datasetUpdateError'));
    }
  };

  const handleDeleteDataset = async () => {
    if (window.confirm(t('testScenarioDetail.confirmDeleteDataset'))) {
      try {
        await onUpdateDataset(test.id, null); // Remove dataset by replacing it with null
        alert(t('testScenarioDetail.datasetDeleted'));
      } catch (error) {
        console.error(t('testScenarioDetail.datasetDeleteError'), error);
        alert(t('testScenarioDetail.datasetDeleteError'));
      }
    }
  };

  return (
    <div>
      <h3>{t('testScenarioDetail.datasetTitle')}</h3>
      <textarea
        value={JSON.stringify(dataset, null, 2)}
        onChange={(e) => setDataset(JSON.parse(e.target.value))}
        rows="10"
        style={{ width: '100%', fontFamily: 'monospace' }}
      />
      <button onClick={handleSave}>{t('testScenarioDetail.save')}</button>
      <button
        onClick={handleDeleteDataset}
        style={{
          marginTop: '1rem',
          background: '#dc2626',
          color: '#fff',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '0.25rem',
          cursor: 'pointer',
        }}
      >
        {t('testScenarioDetail.deleteDataset')}
      </button>
    </div>
  );
};

const TestScenarioDetail = ({
  scenario,
  extractStepsByType,
  onClose,
  onUpdateScenario, // New prop to update scenario
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullCode, setShowFullCode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScenario, setEditedScenario] = useState({
    title: scenario?.title || '',
    description: scenario?.description || '',
    type: scenario?.type || 'acceptance',
    status: scenario?.status || '',
    nodeName: scenario?.nodeName || '',
    ...scenario
  });
  const [isSaving, setIsSaving] = useState(false);

  // Fonction pour sauvegarder les modifications
  const handleSaveChanges = async () => {
    if (!onUpdateScenario) {
      alert(t('testScenarioDetail.updateFunctionNotAvailable'));
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateScenario(editedScenario);
      setIsEditing(false);
      // Optional: show success message
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des modifications');
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour annuler les modifications
  const handleCancelEdit = () => {
    setEditedScenario({
      title: scenario?.title || '',
      description: scenario?.description || '',
      type: scenario?.type || 'acceptance',
      status: scenario?.status || '',
      nodeName: scenario?.nodeName || '',
      ...scenario
    });
    setIsEditing(false);
  };

  if (!scenario) return null;

  const steps = extractStepsByType(scenario);
  const flatSteps = steps.flat();

  // Scenario analysis to bring more value
  const scenarioAnalysis = useMemo(() => {
    const stepTypes = {
      given: flatSteps.filter(step => /^Given/i.test(renderStep(step))).length,
      when: flatSteps.filter(step => /^When/i.test(renderStep(step))).length,
      then: flatSteps.filter(step => /^Then/i.test(renderStep(step))).length,
      and: flatSteps.filter(step => /^And/i.test(renderStep(step))).length,
    };

    const complexity = flatSteps.length <= 3 ? 'Simple' :
      flatSteps.length <= 6 ? 'Moyen' : 'Complexe';

    const estimatedDuration = flatSteps.length * 2; // estimated minutes

    return {
      stepTypes,
      complexity,
      totalSteps: flatSteps.length,
      estimatedDuration
    };
  }, [flatSteps]);

  // Utilise createPortal pour rendre la modal directement dans le body
  return createPortal(
    <div id="test-scenario-detail-modal" className="test-scenario-detail-overlay">
      <div className="test-scenario-detail-container">
        {/* En-tête moderne */}
        <div className="test-scenario-detail-header">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1.5rem'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem'
                }}>
                  🎯
                </div>
                <div>
                  <h2 className="test-scenario-detail-title">
                    {scenario.title || `Scénario de test #${scenario.id || 'N/A'}`}
                  </h2>
                  <p className="test-scenario-detail-subtitle">
                    Analyse détaillée du scénario de test
                  </p>
                </div>
              </div>

              {/* Métriques rapides */}
              <div className="test-scenario-detail-metrics-container">
                <div className="test-scenario-detail-metric-card">
                  <div className="test-scenario-detail-metric-value">
                    {scenarioAnalysis.totalSteps}
                  </div>
                  <div className="test-scenario-detail-metric-label">
                    Étapes
                  </div>
                </div>
                <div className="test-scenario-detail-metric-card">
                  <div className="test-scenario-detail-metric-value">
                    {scenarioAnalysis.complexity}
                  </div>
                  <div className="test-scenario-detail-metric-label">
                    Complexité
                  </div>
                </div>
                <div className="test-scenario-detail-metric-card">
                  <div className="test-scenario-detail-metric-value">
                    ~{scenarioAnalysis.estimatedDuration}min
                  </div>
                  <div className="test-scenario-detail-metric-label">
                    Estimation
                  </div>
                </div>
                {scenario.type && (
                  <div className="test-scenario-detail-metric-card">
                    <div className="test-scenario-detail-metric-value">
                      {scenario.type === 'automatisé' ? '🤖' :
                        scenario.type === 'exploratoire' ? '🔍' : '✅'}
                    </div>
                    <div className="test-scenario-detail-metric-label">
                      {scenario.type.charAt(0).toUpperCase() + scenario.type.slice(1)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={onClose}
              className="test-scenario-detail-close-btn"
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              <span>✕</span> Fermer
            </Button>
          </div>
        </div>

        {/* Navigation par onglets */}
        <div className="test-scenario-detail-tabs-nav-wrapper">
          <nav className="test-scenario-detail-tabs-nav">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
              { id: 'edit', label: t('testScenarioDetail.modifyScenario'), icon: '✏️' },
              { id: 'flow', label: 'Flux visuel', icon: '🔄' },
              { id: 'code', label: t('testScenarioDetail.generatedCode'), icon: '💻' },
              { id: 'analysis', label: 'Analyse', icon: '🔍' },
              { id: 'dataset', label: t('testScenarioDetail.datasets'), icon: '📂' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`test-scenario-detail-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Contenu des onglets */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '2rem'
        }}>
          {activeTab === 'overview' && (
            <div className="test-scenario-detail-tab-content">
              {/* Informations du scénario */}
              <div className="test-scenario-detail-info-section">
                <h3 className="test-scenario-detail-info-title">
                  <span>ℹ️</span> Informations du scénario
                </h3>

                <div className="test-scenario-detail-info-grid">
                  {scenario.nodeName && (
                    <div className="test-scenario-detail-info-card">
                      <div className="test-scenario-detail-info-label">
                        Node associé
                      </div>
                      <div className="test-scenario-detail-info-value">
                        📋 {scenario.nodeName}
                      </div>
                    </div>
                  )}

                  {scenario.status && (
                    <div className="test-scenario-detail-info-card">
                      <div className="test-scenario-detail-info-label">
                        Statut
                      </div>
                      <div className="test-scenario-detail-info-value">
                        {scenario.status}
                      </div>
                    </div>
                  )}

                  <div className="test-scenario-detail-info-card">
                    <div className="test-scenario-detail-info-label">
                      Répartition des étapes
                    </div>
                    <div className="test-scenario-detail-step-tags">
                      {Object.entries(scenarioAnalysis.stepTypes).map(([type, count]) =>
                        count > 0 && (
                          <span key={type} className={`test-scenario-detail-step-tag step-tag-${type}`}>
                            {type.toUpperCase()}: {count}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Résumé des étapes */}
              <div className="test-scenario-detail-overview-section">
                <h3 className="test-scenario-detail-section-title">
                  <span>📋</span> Aperçu des étapes
                </h3>

                <div className="test-scenario-detail-steps-list">
                  {flatSteps.slice(0, 5).map((step, i) => {
                    const stepText = renderStep(step);
                    const stepType = /^Given/i.test(stepText) ? 'given' :
                      /^When/i.test(stepText) ? 'when' :
                        /^Then/i.test(stepText) ? 'then' : 'and';

                    return (
                      <div key={i} className="test-scenario-detail-step-item">
                        <div className={`test-scenario-detail-step-badge step-badge-${stepType}`}>
                          {i + 1}
                        </div>
                        <div className="test-scenario-detail-step-text">
                          {stepText}
                        </div>
                      </div>
                    );
                  })}

                  {flatSteps.length > 5 && (
                    <div className="test-scenario-detail-more-steps">
                      ... et {flatSteps.length - 5} autres étapes
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'edit' && (
            <div className="test-scenario-detail-tab-content">
              <div className="test-scenario-detail-edit-header">
                <h3 className="test-scenario-detail-section-title">
                  <span>✏️</span> {t('testScenarioDetail.editScenario')}
                </h3>

                <div className="test-scenario-detail-actions">
                  {isEditing ? (
                    <>
                      <Button
                        onClick={handleCancelEdit}
                        className="test-scenario-detail-action-btn secondary"
                      >
                        <span>✕</span> {t('testScenarioDetail.cancel')}
                      </Button>
                      <Button
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className={`test-scenario-detail-action-btn primary ${isSaving ? 'loading' : ''}`}
                      >
                        <span>{isSaving ? '⏳' : '💾'}</span>
                        {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setIsEditing(true)}
                      className="test-scenario-detail-action-btn primary"
                    >
                      <span>✏️</span> {t('testScenarioDetail.edit')}
                    </Button>
                  )}
                </div>
              </div>

              {/* Formulaire d'édition */}
              <div className="test-scenario-detail-overview-section">
                <div className="test-scenario-detail-form-grid">
                  {/* Titre */}
                  <div>
                    <label className="test-scenario-detail-form-label">
                      Titre du scénario
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedScenario.title}
                        onChange={(e) => setEditedScenario({
                          ...editedScenario,
                          title: e.target.value
                        })}
                        className="test-scenario-detail-form-input"
                        placeholder={t('testScenarioDetail.scenarioTitlePlaceholder')}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {scenario.title || t('testScenarioDetail.noTitleDefined')}
                      </div>
                    )}
                  </div>

                  {/* Type de test */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      {t('testForms.testType')}
                    </label>
                    {isEditing ? (
                      <select
                        value={editedScenario.type}
                        onChange={(e) => setEditedScenario({
                          ...editedScenario,
                          type: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '1rem',
                          background: '#ffffff',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      >
                        <option value="acceptance">✅ Acceptance</option>
                        <option value="automatisé">{t('testScenarioDetail.automatedType')}</option>
                        <option value="exploratoire">🔍 Exploratoire</option>
                      </select>
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {scenario.type === 'automatisé' && '🤖 '}
                        {scenario.type === 'exploratoire' && '🔍 '}
                        {scenario.type === 'acceptance' && '✅ '}
                        {scenario.type?.charAt(0).toUpperCase() + scenario.type?.slice(1) || t('testScenarioDetail.notDefined')}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Statut
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedScenario.status}
                        onChange={(e) => setEditedScenario({
                          ...editedScenario,
                          status: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '1rem',
                          transition: 'border-color 0.2s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder={t('testScenarioDetail.statusPlaceholder')}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {scenario.status || t('testScenarioDetail.noStatusDefined')}
                      </div>
                    )}
                  </div>

                  {/* Node associé */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Node associé
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedScenario.nodeName}
                        onChange={(e) => setEditedScenario({
                          ...editedScenario,
                          nodeName: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '1rem',
                          transition: 'border-color 0.2s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder={t('testScenarioDetail.nodePlaceholder')}
                      />
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {scenario.nodeName || t('testScenarioDetail.noNodeAssociated')}
                      </div>
                    )}
                  </div>

                  {/* Description/Scénario */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Scénario (format Gherkin)
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editedScenario.description}
                        onChange={(e) => setEditedScenario({
                          ...editedScenario,
                          description: e.target.value
                        })}
                        rows="12"
                        style={{
                          width: '100%',
                          padding: '1rem',
                          border: '2px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontFamily: '"Fira Code", "Courier New", monospace',
                          transition: 'border-color 0.2s ease',
                          outline: 'none',
                          resize: 'vertical'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        placeholder={t('testScenarioDetail.gherkinPlaceholder')}
                      />
                    ) : (
                      <div style={{
                        padding: '1rem',
                        background: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontFamily: '"Fira Code", "Courier New", monospace',
                        color: '#e5e7eb',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}>
                        {scenario.description || 'Aucune description disponible'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Aperçu des étapes en temps réel pendant l'édition */}
                {isEditing && editedScenario.description && (
                  <div style={{
                    marginTop: '2rem',
                    padding: '1.5rem',
                    background: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: '1rem'
                  }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      margin: '0 0 1rem 0',
                      color: '#0c4a6e',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span>👁️</span> Aperçu des étapes
                    </h4>

                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      {editedScenario.description.split('\n')
                        .map(line => line.trim())
                        .filter(line => line && !line.startsWith('Scenario:'))
                        .slice(0, 5)
                        .map((step, i) => {
                          const stepType = /^Given/i.test(step) ? 'given' :
                            /^When/i.test(step) ? 'when' :
                              /^Then/i.test(step) ? 'then' : 'and';

                          return (
                            <div key={i} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '1rem',
                              padding: '0.75rem',
                              background: '#ffffff',
                              borderRadius: '0.5rem',
                              border: `1px solid ${stepType === 'given' ? '#fbbf24' :
                                  stepType === 'when' ? '#60a5fa' :
                                    stepType === 'then' ? '#34d399' : '#9ca3af'
                                }`
                            }}>
                              <div style={{
                                background: stepType === 'given' ? '#fbbf24' :
                                  stepType === 'when' ? '#60a5fa' :
                                    stepType === 'then' ? '#34d399' : '#9ca3af',
                                color: '#ffffff',
                                borderRadius: '50%',
                                width: '1.5rem',
                                height: '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                flexShrink: 0
                              }}>
                                {i + 1}
                              </div>
                              <div style={{
                                fontSize: '0.875rem',
                                color: '#374151'
                              }}>
                                {step}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'flow' && (
            <div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: '0 0 2rem 0',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span>🔄</span> Flux visuel du scénario
              </h3>
              <div style={{
                background: '#ffffff',
                borderRadius: '1rem',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                minHeight: '400px'
              }}>
                <TestFlowVisualizer testScenario={scenario} />
              </div>
            </div>
          )}

          {activeTab === 'code' && (
            <div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                margin: '0 0 2rem 0',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'space-between'
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>💻</span> Code généré
                </span>
                {scenario.code && (
                  <button
                    onClick={() => setShowFullCode(!showFullCode)}
                    style={{
                      background: '#3b82f6',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {showFullCode ? t('testScenarioDetail.reduceCode') : t('testScenarioDetail.seeAll')}
                  </button>
                )}
              </h3>

              {scenario.code ? (
                <div style={{
                  background: '#1f2937',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  border: '1px solid #374151'
                }}>
                  <div style={{
                    background: '#374151',
                    padding: '1rem 1.5rem',
                    color: '#d1d5db',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    borderBottom: '1px solid #4b5563',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>📄</span> Code du test automatisé
                  </div>
                  <pre style={{
                    color: '#e5e7eb',
                    padding: '2rem',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    overflow: 'auto',
                    maxHeight: showFullCode ? 'none' : '300px',
                    margin: '0',
                    fontFamily: '"Fira Code", "Courier New", monospace'
                  }}>
                    {scenario.code}
                  </pre>
                </div>
              ) : (
                <div style={{
                  background: '#f9fafb',
                  borderRadius: '1rem',
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  border: '2px dashed #d1d5db'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem',
                    opacity: 0.5
                  }}>
                    📝
                  </div>
                  <h4 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    margin: '0 0 0.5rem 0',
                    color: '#374151'
                  }}>
                    Aucun code généré
                  </h4>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#6b7280',
                    margin: '0'
                  }}>
                    Ce scénario n'a pas encore de code automatisé généré.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analysis' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid #0ea5e9'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  margin: '0 0 1.5rem 0',
                  color: '#0c4a6e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>🔍</span> Analyse de la qualité
                </h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem'
                }}>
                  <div style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '0.75rem',
                    textAlign: 'center',
                    border: '1px solid #0ea5e9'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '700',
                      color: scenarioAnalysis.complexity === 'Simple' ? '#10b981' :
                        scenarioAnalysis.complexity === 'Moyen' ? '#f59e0b' : '#ef4444',
                      marginBottom: '0.5rem'
                    }}>
                      {scenarioAnalysis.complexity === 'Simple' ? '✅' :
                        scenarioAnalysis.complexity === 'Moyen' ? '⚠️' : '🔴'}
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '0.25rem'
                    }}>
                      {scenarioAnalysis.complexity}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      Niveau de complexité
                    </div>
                  </div>

                  <div style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '0.75rem',
                    textAlign: 'center',
                    border: '1px solid #0ea5e9'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '700',
                      color: '#3b82f6',
                      marginBottom: '0.5rem'
                    }}>
                      {scenarioAnalysis.totalSteps}
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '0.25rem'
                    }}>
                      Étapes
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      Nombre total
                    </div>
                  </div>

                  <div style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '0.75rem',
                    textAlign: 'center',
                    border: '1px solid #0ea5e9'
                  }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '700',
                      color: '#8b5cf6',
                      marginBottom: '0.5rem'
                    }}>
                      ~{scenarioAnalysis.estimatedDuration}min
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '0.25rem'
                    }}>
                      Durée estimée
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6b7280'
                    }}>
                      Temps d'exécution
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommandations */}
              <div style={{
                background: '#ffffff',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  margin: '0 0 1.5rem 0',
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>💡</span> Recommandations
                </h3>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {scenarioAnalysis.totalSteps > 8 && (
                    <div style={{
                      background: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '0.75rem',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                      <div>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#92400e',
                          marginBottom: '0.25rem'
                        }}>
                          Scénario complexe
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#78350f'
                        }}>
                          Considérez diviser ce scénario en plusieurs tests plus simples pour une meilleure maintenabilité.
                        </div>
                      </div>
                    </div>
                  )}

                  {!scenario.code && (
                    <div style={{
                      background: '#dbeafe',
                      border: '1px solid #3b82f6',
                      borderRadius: '0.75rem',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>💻</span>
                      <div>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#1e40af',
                          marginBottom: '0.25rem'
                        }}>
                          Automatisation possible
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#1e3a8a'
                        }}>
                          {t('testScenarioDetail.automationSuggestion')}
                        </div>
                      </div>
                    </div>
                  )}

                  {scenarioAnalysis.stepTypes.given === 0 && (
                    <div style={{
                      background: '#fee2e2',
                      border: '1px solid #ef4444',
                      borderRadius: '0.75rem',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>🔴</span>
                      <div>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#dc2626',
                          marginBottom: '0.25rem'
                        }}>
                          Préconditions manquantes
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#b91c1c'
                        }}>
                          Ajoutez des étapes "Given" pour définir clairement les préconditions du test.
                        </div>
                      </div>
                    </div>
                  )}

                  {scenarioAnalysis.stepTypes.then === 0 && (
                    <div style={{
                      background: '#fee2e2',
                      border: '1px solid #ef4444',
                      borderRadius: '0.75rem',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>🔴</span>
                      <div>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#dc2626',
                          marginBottom: '0.25rem'
                        }}>
                          Vérifications manquantes
                        </div>
                        <div style={{
                          fontSize: '0.875rem',
                          color: '#b91c1c'
                        }}>
                          Ajoutez des étapes "Then" pour définir clairement les résultats attendus.
                        </div>
                      </div>
                    </div>
                  )}

                  {scenarioAnalysis.stepTypes.given > 0 &&
                    scenarioAnalysis.stepTypes.when > 0 &&
                    scenarioAnalysis.stepTypes.then > 0 &&
                    scenarioAnalysis.totalSteps <= 6 && (
                      <div style={{
                        background: '#dcfce7',
                        border: '1px solid #10b981',
                        borderRadius: '0.75rem',
                        padding: '1rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}>
                        <span style={{ fontSize: '1.5rem' }}>✅</span>
                        <div>
                          <div style={{
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#065f46',
                            marginBottom: '0.25rem'
                          }}>
                            Scénario bien structuré
                          </div>
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#047857'
                          }}>
                            Ce scénario suit les bonnes pratiques avec une structure Given-When-Then claire et concise.
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'dataset' && (
            <DatasetManager
              test={scenario}
              onUpdateDataset={async (testId, dataset) => {
                await apiClient.post(`/node/${scenario.nodeName}/tests/${testId}/dataset`, { dataset });
                // Refresh data after update
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TestScenarioDetail;